import { SWRConfig } from 'swr';
import { fetchGoalInfo } from '@api/fetchers/goal';
import SimpleLayout from '@layout/SimpleLayout/SimpleLayout';
import Head from 'next/head';
import SEO from '@seo/index';
import FreeLesson from '@cont/Content/FreeLesson';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import {
  fetchLessonDetails,
  fetchPlaylistDetails
} from '@api/fetchers/content';
import useFullLessonDetails from '@cont/Content/FreeLesson/useFullLessonDetails';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { useEffect } from 'react';
import { fetchEducatorLevelDisplayConfig } from '@api/fetchers/profile';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import {
  convertTitleToSlug,
  getCuratedClassesSchema
} from '@cont/Content/FreeLesson/utils';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';

const FreeLessonPage = ({
  goalUID,
  playlistUID,
  fallbackData,
  lessonDetailsInfo
}) => {
  const { keys, revalidateLessonDetails, revalidatePlaylistDetails } =
    useFullLessonDetails(playlistUID, fallbackData);
  const { goalInfo } = useGoalInfo(goalUID);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { name: metaTitle, description: metaDescription } =
    lessonDetailsInfo || {};

  const slug = convertTitleToSlug(metaTitle) || '-';

  const url = `https://unacademy.com/curated-classes/${slug}/${playlistUID}`;

  useEffect(() => {
    if (isLoggedIn) {
      revalidatePlaylistDetails();
      revalidateLessonDetails();
    }
  }, [isLoggedIn, revalidateLessonDetails, revalidatePlaylistDetails]);

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [keys.lessonDetailsSWRKey]: fallbackData.lessonDetails,
          [keys.lessonPlayListDetailsSWRKey]: fallbackData.playlistDetails,
          [keys.levelDisplayInfoKey]: fallbackData.levelsDisplayInfo
        }
      }}
    >
      <SEO
        title={metaTitle}
        url={url}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: { metaDescription }
        }}
        schema={getCuratedClassesSchema({
          goalInfo,
          playlistUID,
          slug,
          lessonDetailsInfo,
          playlistDetails: fallbackData?.playlistDetails
        })}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <FreeLesson goalUID={goalUID} playlistUID={playlistUID} slug={slug} />
    </SWRConfig>
  );
};

FreeLessonPage.pageInfo = {
  name: 'Free Lesson Page',
  isMWebSupported: true,
  layoutOptions: {
    hideFreshbot: true,
    disableDarkMode: true
  }
};

export const getStaticProps = async ({ params }) => {
  try {
    const { playlistUID, playlistTitle } = params || {};
    const lessonDetails = await fetchLessonDetails(playlistUID);
    const promises = [];
    promises.push(
      resolvePromiseToObject(
        fetchPlaylistDetails(playlistUID),
        'playlistDetails'
      ),
      resolvePromiseToObject(
        fetchEducatorLevelDisplayConfig(),
        'levelsDisplayInfo'
      ),
      resolvePromiseToObject(
        fetchGoalInfo(lessonDetails?.data?.[0]?.topologyUid),
        'goalInfo'
      )
    );
    const fallbackData = await Promise.all(promises).then((resolvedValues) =>
      resolvedValues.reduce(
        (acc, next) => ({ ...acc, [next.type]: next.result }),
        {}
      )
    );
    const goalInfo = fallbackData?.goalInfo || {};

    const lessonDetailsInfo = lessonDetails?.data?.[0] || {};
    const correctSlug =
      lessonDetailsInfo?.slug || lessonDetailsInfo?.name
        ? convertTitleToSlug(lessonDetailsInfo?.name)
        : '-';
    /**
     * Redirect in case the slug is not correct
     */
    if (correctSlug !== playlistTitle) {
      return {
        redirect: {
          destination: `/curated-classes/${correctSlug}/${playlistUID}`,
          permanent: true
        }
      };
    }
    return {
      props: {
        goalUID: lessonDetails?.data?.[0]?.topologyUid,
        goalInfo,
        playlistUID,
        lessonDetailsInfo,
        fallbackData: {
          lessonDetails,
          ...fallbackData
        },
        globalFallbackData: {
          [getTopologyKey(goalInfo.uid)]: goalInfo
        }
      }
    };
  } catch (error) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

const Layout = ({ children }) => (
  <SimpleLayout layoutOptions={{ showLogin: true, showFooter: false }}>
    {children}
  </SimpleLayout>
);

export default FreeLessonPage;
FreeLessonPage.Layout = Layout;
FreeLessonPage.Wrapper = null;
